/**
 * Spatial hash för grannsökning — O(n) att bygga, O(1) per cell-lookup.
 * Byggs om varje tick (inga persistenta allokeringar mellan ticks ännu;
 * objektpooler införs när profilering visar behov). Deterministisk:
 * units läggs in i arrayordning (stigande id) och läses ut i samma ordning.
 */
import type { Unit } from './state';

export class SpatialHash {
  private readonly cellSize: number;
  private readonly cells = new Map<number, Unit[]>();

  constructor(cellSizeFp: number) {
    this.cellSize = cellSizeFp;
  }

  private key(cx: number, cy: number): number {
    // Kartan är ~96x54 world units; cellindex ryms gott i 16 bitar per axel.
    return (cx + 512) * 65536 + (cy + 512);
  }

  insert(unit: Unit): void {
    const cx = Math.floor(unit.x / this.cellSize);
    const cy = Math.floor(unit.y / this.cellSize);
    const k = this.key(cx, cy);
    const cell = this.cells.get(k);
    if (cell) cell.push(unit);
    else this.cells.set(k, [unit]);
  }

  /**
   * Alla units vars cell ligger inom radien (grov filtrering — anroparen
   * avståndstestar själv). Ordningen är deterministisk: cellerna gås igenom
   * i fast ordning och varje cell håller insättningsordning.
   */
  queryCircle(x: number, y: number, radiusFp: number, out: Unit[]): Unit[] {
    out.length = 0;
    const minCx = Math.floor((x - radiusFp) / this.cellSize);
    const maxCx = Math.floor((x + radiusFp) / this.cellSize);
    const minCy = Math.floor((y - radiusFp) / this.cellSize);
    const maxCy = Math.floor((y + radiusFp) / this.cellSize);
    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cy = minCy; cy <= maxCy; cy++) {
        const cell = this.cells.get(this.key(cx, cy));
        if (cell) for (const u of cell) out.push(u);
      }
    }
    return out;
  }
}

/** Bygger en spatial hash över alla levande units. */
export function buildSpatialHash(units: Unit[], cellSizeFp: number): SpatialHash {
  const hash = new SpatialHash(cellSizeFp);
  for (const u of units) hash.insert(u);
  return hash;
}
